import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditCommissionPolicyDialog({ policy, open, onOpenChange, onSave }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [paidStatuses, setPaidStatuses] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (policy && open) {
      loadStatuses();
      loadPaidStatuses();
    }
  }, [policy, open]);

  useEffect(() => {
    if (policy && statuses.length > 0) {
      // Try to find the status ID from the loaded statuses
      const statusId = policy.policyStatus_id || policy.status_id || 
                       statuses.find(s => s.label === (policy.policyStatus || policy.status_name))?.value || 
                       '';
      
      setFormData({
        policy_id: policy.id,
        client: policy.client || '',
        policy_number: policy.policy_number || '',
        policyStatus: statusId,
        paid_status: policy.paid_status || '',
        isLocked: policy.isLocked || false
      });
    }
  }, [policy, statuses]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/pms/status');
      const normalizedStatuses = Array.isArray(response.data) 
        ? response.data.map(s => ({ value: s.id, label: s.status || s.name }))
        : [];
      setStatuses(normalizedStatuses);
      setLoading(false);
    } catch (err) {
      console.error('Error loading statuses:', err);
      toast.error('Failed to load policy statuses');
      setLoading(false);
    }
  };

  const loadPaidStatuses = async () => {
    try {
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/policy/paidStatus');
      const normalizedPaidStatuses = Array.isArray(response.data) 
        ? response.data.map(s => ({ value: s.id, label: s.status }))
        : [];
      setPaidStatuses(normalizedPaidStatuses);
    } catch (err) {
      console.error('Error loading paid statuses:', err);
      toast.error('Failed to load paid statuses');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.client || !formData.policy_number || !formData.policyStatus) {
      toast.error('Please fill in all required fields');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmSubmit = async () => {
    try {
      setSaving(true);
      
      const formDataObject = {
        client: formData.client || null,
        policy_number: formData.policy_number || null,
        policyStatus: formData.policyStatus || null,
        paid_status: formData.paid_status || null,
        isLocked: formData.isLocked
      };

      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/commission/policiesUpdate',
        {
          policy_id: formData.policy_id,
          formData: formDataObject
        }
      );

      toast.success('Policy updated successfully');
      onSave && onSave();
      setShowConfirmation(false);
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating policy:', err);
      toast.error('Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Policy</DialogTitle>
          <DialogDescription>
            Update policy information. Policy can be edited even if locked.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Client Name */}
              <div className="space-y-2">
                <Label htmlFor="client">
                  Client Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="client"
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  placeholder="Enter client name"
                  required
                />
              </div>

              {/* Policy Number */}
              <div className="space-y-2">
                <Label htmlFor="policy_number">
                  Policy Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="policy_number"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  placeholder="Enter policy number"
                  required
                />
              </div>

              {/* Policy Status */}
              <div className="space-y-2">
                <Label htmlFor="status">
                  Policy Status <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.policyStatus}
                  onValueChange={(value) => setFormData({ ...formData, policyStatus: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Paid Status */}
              <div className="space-y-2">
                <Label htmlFor="paid_status">
                  Paid Status
                </Label>
                <Select
                  value={formData.paid_status}
                  onValueChange={(value) => setFormData({ ...formData, paid_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select paid status" />
                  </SelectTrigger>
                  <SelectContent>
                    {paidStatuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lock Policy Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="isLocked">Lock Policy</Label>
                  <p className="text-sm text-neutral-500">
                    Prevent further edits to this policy
                  </p>
                </div>
                <Switch
                  id="isLocked"
                  checked={formData.isLocked}
                  onCheckedChange={(checked) => setFormData({ ...formData, isLocked: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Policy Update</DialogTitle>
          <DialogDescription>
            Please review the updated policy details
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Client:</span>
            <span className="text-sm font-semibold">{formData.client}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Policy Number:</span>
            <span className="text-sm font-semibold">{formData.policy_number}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Policy Status:</span>
            <span className="text-sm font-semibold">
              {statuses.find(s => s.value === formData.policyStatus)?.label || formData.policyStatus}
            </span>
          </div>
          {formData.paid_status && (
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Paid Status:</span>
              <span className="text-sm font-semibold">
                {paidStatuses.find(s => s.value === formData.paid_status)?.label}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Locked:</span>
            <span className="text-sm font-semibold">{formData.isLocked ? 'Yes' : 'No'}</span>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowConfirmation(false)}
            disabled={saving}
          >
            Back
          </Button>
          <Button
            onClick={confirmSubmit}
            disabled={saving}
            className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Confirm & Update'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
